import { execFileSync } from "node:child_process";
import { peerForHost, shellQuote } from "./agents.js";
import { endLinesWithReset, scan, sgrOnly } from "./ansi.js";
import { SSH_OPTIONS } from "./channel.js";
import { tmux, tmuxArgs } from "./mux.js";
import type { Store } from "./store.js";
import type { PaneView } from "./view.js";

/**
 * Glance: the last few lines a pane printed.
 *
 * The cheap half of what "render any pane from the master" hides: a stateless
 * `capture-pane`, not a frame stream -- no resize negotiation, arbitrary key
 * routing, or reconnect. Dash can submit a buffered prompt, but that remains one
 * stateless command rather than a terminal protocol. That deferral is what keeps
 * murmur a state layer rather than a
 * multiplexer (see ARCHITECTURE.md's non-goals), and why this file is thirty
 * lines instead of most of herdr.
 */

const GLANCE_LINES = 40;

/** Terminal default tab stop, and what a pane's own output was laid out for. */
const TAB_WIDTH = 8;

/**
 * Expand tabs to spaces, per line, at the terminal's 8-column stops.
 *
 * A tab is one character and up to eight columns. Every width-aware consumer
 * here -- ink's `truncate-end`, via `string-width` -- scores it as 2, so a line
 * measured as fitting the glance box was advanced by the terminal to the next
 * tab stop, overflowed, wrapped, and pushed every row below it down. Tabs are
 * ordinary in pane output (`git status`, `make`, most log formats), so the dash
 * mis-rendered on unremarkable content.
 *
 * Expanded rather than stripped or replaced 1:1, because the columns are the
 * point of a tab: collapsing them would shear exactly the table-shaped output
 * that uses them. Counted per line, since a tab stop is measured from the start
 * of the row.
 *
 * The column counter advances on printable text only. Since `capture-pane -e`,
 * a `git status` line arrives with a colour prefix in front of it, and counting
 * those bytes moved every stop on the line to the left.
 */
export function expandTabs(text: string, width = TAB_WIDTH): string {
  if (!text.includes("\t")) return text;
  return text
    .split("\n")
    .map((line) => {
      let out = "";
      let column = 0;
      for (const token of scan(line)) {
        if (token.kind === "sgr") {
          out += token.value;
          continue;
        }
        for (const character of token.value) {
          if (character === "\t") {
            const advance = width - (column % width);
            out += " ".repeat(advance);
            column += advance;
          } else {
            out += character;
            column += 1;
          }
        }
      }
      return out;
    })
    .join("\n");
}

/**
 * What a capture must pass through before anything paints it.
 *
 * Two rules, in this order. SGR only, because the preview is a fixed box inside
 * the dash rather than a terminal: a cursor move or erase paints outside it and
 * corrupts the chrome, and an OSC 52 in pane output would write the operator's
 * clipboard once per redraw. Then a reset per line, because tmux routinely
 * captures output mid-attribute and an unterminated background bled into the
 * border and every row the dash drew after it.
 *
 * Tabs are expanded between the two: the stops are visible columns, so they must
 * be counted after the non-SGR bytes are gone.
 */
function previewSafe(captured: string): string {
  return endLinesWithReset(expandTabs(sgrOnly(captured)));
}

/**
 * The one ssh a glance runs, injectable for the same reason `agents.ts` has
 * `Runner`: without a seam the remote branch cannot be tested without a second
 * machine, and the skip below -- which is the whole point of this file's
 * bug history -- would have no coverage at all.
 */
export type GlanceRunner = (target: string, argv: string[]) => string;

const sshRunner: GlanceRunner = (target, argv) =>
  execFileSync("ssh", [...SSH_OPTIONS, target, ...argv], {
    encoding: "utf8",
    timeout: 3000,
    stdio: ["ignore", "pipe", "ignore"],
  });

export function glance(
  store: Store,
  agent: PaneView,
  lines = GLANCE_LINES,
  run: GlanceRunner = sshRunner,
): string | null {
  if (agent.local) {
    const local = tmux.capture(agent.pane, lines, agent.server);
    return local === null ? null : previewSafe(local);
  }

  const resolved = peerForHost(store, agent.host_id);
  if (!resolved) return null;
  const { peer, target } = resolved;
  // Never dial a peer whose last attempt failed.
  //
  // The preview runs PER KEYPRESS as the cursor moves, so this ssh is on the
  // interactive path in a way the collector's never is. A host that cannot
  // authenticate costs the full exchange to fail -- measured at ~1.5s against a
  // real peer rejecting keyboard-interactive, against 70ms for a local row --
  // and every pass over that row paid it again. That was "the picker is slow".
  //
  // `last_error` is the right question and costs nothing: the store clears it on
  // every successful fetch, so a non-null value means the MOST RECENT collect
  // failed. Not keyed on `freshness`, which is a clock: a peer can be stale
  // merely because the floor has not come round yet, and skipping it would
  // withhold a glance from a host that answers fine.
  //
  // The row itself still previews. Its metadata is cached and worth reading --
  // and a peer whose fetch failed keeps its last-known snapshot, which is
  // exactly why the row is on screen at all.
  if (peer.last_error !== null) return null;
  try {
    // ssh's remote side is a login shell. Build the tmux argv first, including
    // the snapshot's server selector, then quote every word into its one remote
    // command so hostile server values and pane ids remain data. `-e` keeps the
    // same styling local and remote; previewSafe accepts SGR and strips the rest.
    const argv = [
      "tmux",
      ...tmuxArgs(agent.server, ["capture-pane", "-p", "-e", "-t", agent.pane, "-S", `-${lines}`]),
    ];
    return previewSafe(run(target, [argv.map(shellQuote).join(" ")]));
  } catch {
    // Cold socket, dead tmux, gone pane. The preview says so rather than the
    // picker failing.
    return null;
  }
}
